import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { 
  Target, 
  Building2, 
  MapPin, 
  TrendingUp,
  RefreshCw,
  Eye,
  CheckCircle,
  Award
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const InvestorMatches = ({ investorId, onViewActor }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState(20);
  const [totalMatches, setTotalMatches] = useState(0);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/social-investors/${investorId}/matches?min_score=${minScore}&limit=20`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setMatches(response.data.matches || []);
      setTotalMatches(response.data.total_matches || 0);
    } catch (error) {
      console.error('Failed to fetch matches:', error);
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (investorId) {
      fetchMatches();
    }
  }, [investorId]);

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-700 bg-green-50';
    if (score >= 50) return 'text-blue-600 bg-blue-50';
    if (score >= 30) return 'text-yellow-700 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getProgressColor = (score) => {
    if (score >= 70) return 'bg-green-700';
    if (score >= 50) return 'bg-blue-500';
    if (score >= 30) return 'bg-yellow-700';
    return 'bg-gray-400';
  };

  const certificationLabels = {
    level_3_excellence: { label: 'Level A', color: 'bg-amber-100 text-amber-800' },
    level_2_commitment: { label: 'Level B', color: 'bg-blue-100 text-blue-800' },
    level_1_basic: { label: 'Level C', color: 'bg-green-100 text-green-800' },
    none: { label: 'Not Certified', color: 'bg-gray-100 text-gray-600' }
  };

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">Minimum Match Score</Label>
            <span className="text-sm font-bold text-emerald-700">{minScore}%</span>
          </div>
          <div className="flex items-center gap-4">
            <Slider
              value={[minScore]}
              onValueChange={(v) => setMinScore(v[0])}
              min={0}
              max={80}
              step={5}
              className="flex-1"
            />
            <Button size="sm" onClick={fetchMatches} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Found <span className="font-semibold text-emerald-700">{totalMatches}</span> potential matches
        </span>
        <span>Showing top 20</span>
      </div>

      {/* Matches List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-emerald-700" />
          <span className="ml-2 text-gray-500">Finding matches...</span>
        </div>
      ) : matches.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No matches found</p>
            <p className="text-sm mt-1">Try lowering the minimum score threshold</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {matches.map((match, index) => {
            // Backend returns a flat shape (actor_id, organization_name, city,
            // country, match_score, score_breakdown, matching_sdgs,
            // certification_level). Normalise to what this component expects.
            const actor = {
              id: match.actor_id,
              organization_name: match.organization_name,
              organization_type: match.organization_type,
              city: match.city,
              country: match.country,
              certification_level: match.certification_level,
              social_mission: match.social_mission,
            };
            const breakdown = match.score_breakdown || {};
            const criterionLabels = {
              sdg_alignment: 'SDG alignment',
              geographic_match: 'Geographic match',
              sector_match: 'Sector match',
              investment_size_match: 'Investment size match',
              esg_alignment: 'ESG alignment',
              non_financial_match: 'Non-financial match',
            };
            const reasons = Object.entries(breakdown)
              .filter(([, v]) => v && typeof v === 'object' && 'score' in v && 'max' in v)
              .map(([key, v]) => ({
                criterion: criterionLabels[key] || key.replace(/_/g, ' '),
                score: v.score,
                max: v.max,
              }));
            const matchData = {
              percentage: Math.round(match.match_score || 0),
              quality: match.match_quality,
              reasons,
              sdg_overlap: match.matching_sdgs || [],
            };
            const cert = certificationLabels[actor.certification_level] || certificationLabels.none;
            
            return (
              <Card key={actor.id || index} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Rank Badge */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${getScoreColor(matchData.percentage)}`}>
                      {index + 1}
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900 line-clamp-1">
                            {actor.organization_name}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                            <MapPin className="h-3 w-3" />
                            {actor.city}, {actor.country}
                          </div>
                        </div>
                        <Badge className={cert.color}>
                          <Award className="h-3 w-3 mr-1" />
                          {cert.label}
                        </Badge>
                      </div>

                      {/* Match Score */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600">Match Score</span>
                          <span className={`font-bold ${matchData.percentage >= 50 ? 'text-emerald-700' : 'text-gray-600'}`}>
                            {matchData.percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${getProgressColor(matchData.percentage)}`}
                            style={{ width: `${matchData.percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* Match Reasons */}
                      <div className="space-y-1 mb-3">
                        {(matchData.reasons || []).map((reason, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs">
                            <span className="text-gray-600 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              {reason.criterion}
                            </span>
                            <span className="font-medium">{reason.score}/{reason.max}</span>
                          </div>
                        ))}
                      </div>

                      {/* SDG Overlap */}
                      {matchData.sdg_overlap && matchData.sdg_overlap.length > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          <Target className="h-4 w-4 text-emerald-700" />
                          <div className="flex flex-wrap gap-1">
                            {matchData.sdg_overlap.map(sdg => (
                              <Badge key={sdg} variant="outline" className="text-xs bg-emerald-50">
                                SDG {sdg}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Mission Preview */}
                      {actor.social_mission && (
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {actor.social_mission}
                        </p>
                      )}
                    </div>

                    {/* View Button */}
                    {onViewActor && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewActor(actor.id)}
                        className="flex-shrink-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InvestorMatches;
